import CustomerHome from "@/components/customer/Home";
import DriverHome from "@/components/driver/Home";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { isCustomerRole, isDriverRole } from "@/lib/roles";
import bookingService from "@/lib/services/bookings";
import { useAuthStore } from "@/stores/use-auth-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { router, useRootNavigationState } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import { Modal, StyleSheet, TouchableOpacity, View } from "react-native";

export default function HomeScreen() {
  const { user, theme } = useAuth();
  const desktop = useDesktopLayout();
  const rootNavigationState = useRootNavigationState();
  const checkedActiveBookingForUserId = useRef<number | null>(null);
  const [activeBookingModalId, setActiveBookingModalId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!rootNavigationState?.key) return;

    if (!user) return;

    if (isDriverRole(user.role)) {
      const needsOnboarding =
        !user.stripe_account?.details_submitted || !user.driver?.is_verified ||
        (user.vehicles_count || 0) === 0;

      if (needsOnboarding) {
        setTimeout(() => {
          router.replace("/complete-registration");
        }, 1000);
      }
    }

    if (user.provider_id && (!user.phone || !user.address)) {
      setTimeout(() => {
        router.replace("/complete-oauth");
      }, 1000);
    }
  }, [user, rootNavigationState]);

  useEffect(() => {
    if (!user) {
      checkedActiveBookingForUserId.current = null;
      return;
    }

    if (!rootNavigationState?.key) return;

    if (checkedActiveBookingForUserId.current === user.id) return;

    const needsDriverOnboarding =
      isDriverRole(user.role) &&
      (!user.stripe_account?.details_submitted ||
        !user.driver?.is_verified ||
        (user.vehicles_count || 0) === 0);
    const needsOAuthCompletion =
      !!user.provider_id && (!user.phone || !user.address);

    if (needsDriverOnboarding || needsOAuthCompletion) return;

    checkedActiveBookingForUserId.current = user.id;

    bookingService
      .getBookings({ status: "active" })
      .then(({ data }) => {
        const booking = data?.[0];
        if (!booking) return;

        useAuthStore.getState().setActiveBooking(booking.id);
        setActiveBookingModalId(booking.id);
      })
      .catch(() => {});
  }, [user, rootNavigationState]);

  if (!user) {
    return <Loader />;
  }

  if (isDriverRole(user.role)) {
    const needsOnboarding =
      !user.stripe_account?.details_submitted ||
      (user.vehicles_count || 0) === 0;

    if (needsOnboarding) {
      return <Loader />;
    }
  }

  if (user.provider_id && (!user.phone || !user.address)) {
    return <Loader />;
  }

  return (
    <>
      {isCustomerRole(user.role) ? <CustomerHome /> : <DriverHome />}

      <Modal
        visible={activeBookingModalId !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setActiveBookingModalId(null)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: theme.background },
              desktop && styles.desktopModalContent,
            ]}
          >
            <View style={styles.modalHeader}>
              <Ionicons
                name="information-circle"
                size={40}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.modalTitle} type="subtitle">
                You have an active booking
              </ThemedText>
            </View>

            <ThemedText style={styles.modalDescription}>
              Your booking is still in progress. Open it to check its status,
              contact the other party, or continue the job.
            </ThemedText>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.primaryButton]}
                onPress={() => {
                  const bookingId = activeBookingModalId;
                  setActiveBookingModalId(null);
                  router.push(`/bookings/${bookingId}`);
                }}
              >
                <ThemedText style={styles.primaryButtonText}>
                  View booking
                </ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.secondaryButton]}
                onPress={() => setActiveBookingModalId(null)}
              >
                <ThemedText style={styles.secondaryButtonText}>
                  Dismiss
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    padding: 20,
  },
  modalContent: {
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  desktopModalContent: {
    width: 400,
    alignSelf: "center",
  },
  modalHeader: {
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    marginTop: 8,
    textAlign: "center",
  },
  modalDescription: {
    textAlign: "center",
    marginBottom: 24,
    color: BrandColors.mediumGray,
    lineHeight: 22,
  },
  modalButtons: {
    gap: 12,
  },
  modalButton: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  primaryButton: {
    backgroundColor: BrandColors.primary,
  },
  primaryButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
  },
  secondaryButtonText: {
    fontWeight: "600",
  },
});
